import { useContext, useCallback, useEffect, useState } from 'react';
import UserContext from '../../../../common/utils/getContext';
import { getMemoizedData } from '../../../../common/utils/memo';
import { getData } from '../../../../common/utils/router';
import { zendoStorage } from '../../../../hybrid/wrappers';

export const getLabel = (opt) => opt?.label || opt?.title || opt?.name || opt?.code;
export const getIdetify = (opt) => {
    if (Array.isArray(opt) && opt?.length) {
        opt = opt[0];
    }
    return opt?.uniqueId !== undefined
        ? opt?.uniqueId
        : opt?.id !== undefined
        ? opt?.id
        : opt?.value !== undefined
        ? opt?.value
        : undefined;
};

export const validValue = (multiple, val, opt) => {
    if (multiple) {
        return Array.isArray(val) && val.map((v) => `${v}`).includes(`${getIdetify(opt)}`);
    }
    return getIdetify(opt) !== undefined && `${getIdetify(opt)}` === `${val}`;
};

export const findOptionByValueId = (val, findedList, allList, multiple) => {
    if (multiple) {
        const getTer = (arr) =>
            arr
                .filter((opt) => validValue(multiple, val, opt))
                .map((opt) => {
                    return {
                        ...opt,
                        label: getLabel(opt),
                        value: getIdetify(opt)
                    };
                });
        return getTer([...new Map(allList.concat(findedList).map((item) => [getIdetify(item), item])).values()]);
    }

    const finded =
        findedList.find((opt) => validValue(multiple, val, opt)) || allList.find((opt) => validValue(multiple, val, opt));
    return finded ? { ...finded, label: getLabel(finded), value: getIdetify(finded) } : finded;
};

export const useLoadOptions = (getOptions, loading, setLoading, nextPageLink, multiple) => {
    const [listState, setList] = useState([]);
    const { userToken, lang } = useContext(UserContext);

    const loadOptions = useCallback(() => {
        if (loading && getOptions?.url) {
            const callBack = async (r) => {
                const normalizeData = (data) => {
                    if (Array.isArray(data)) return data;
                    return Object.values(data);
                };
                const res = typeof r?.json === 'function' ? await r?.json() : r;
                nextPageLink.current = res.links?.next;
                if (res?.data || getOptions?.sorter) {
                    setList(
                        getOptions?.sorter
                            ? getOptions?.sorter(res?.data, res)
                            : normalizeData(res?.data).map((item) => {
                                  if (getOptions?.id) {
                                      return { ...item, id: item[getOptions?.id] };
                                  }
                                  return item;
                              })
                    );
                    setLoading(false);
                }
                if (res?.message?.toLowerCase() === 'unauthenticated' && res?.auth_route) {
                    setLoading(true);
                    zendoStorage.set('unauthenticated', JSON.stringify({ ...res, reloadPage: false }));
                }
            };
            if (getOptions?.disableCache) {
                getData(getOptions?.url, userToken, lang, false, getOptions?.currency || false, false, true).then(callBack);
            } else {
                getMemoizedData(getOptions?.url, userToken, lang, getOptions?.currency || false).then(callBack);
            }
        }
    }, [getOptions, nextPageLink, lang, setList, loading, setLoading, userToken]);

    useEffect(() => {
        if (getOptions?.preparedLoad || multiple) {
            loadOptions();
        }
    }, [getOptions?.preparedLoad, loadOptions, multiple]);

    return { loadOptions, listState, setList };
};

export const searchFunction = (
    v,
    nextPageLink,
    setSearchValue,
    list,
    findList,
    timeout,
    getOptions,
    setLoading,
    userToken,
    lang,
    setGetAllListFinded
) => {
    setSearchValue(v);
    clearTimeout(timeout?.current);
    if (getOptions?.searchMinLength && v?.length < getOptions?.searchMinLength) {
        setLoading(false);
        return;
    }
    const func = () => {
        if (!v) {
            nextPageLink.current = null;
            findList([]);
        } else if (getOptions?.searchUrl) {
            const callBack = (res) => {
                nextPageLink.current = res.links?.next;
                if (res?.data) {
                    setGetAllListFinded(
                        getOptions?.sorter
                            ? getOptions.sorter(res?.data)
                            : res?.data.map((item) => {
                                  if (getOptions?.id) {
                                      return { ...item, id: item[getOptions?.id] };
                                  }
                                  return item;
                              })
                    );
                    findList(
                        getOptions?.sorter
                            ? getOptions.sorter(res?.data)
                            : res?.data.map((item) => {
                                  if (getOptions?.id) {
                                      return { ...item, id: item[getOptions?.id] };
                                  }
                                  return item;
                              })
                    );
                    setLoading(false);
                }
            };
            const url = `${getOptions?.searchUrl}${getOptions?.searchUrl?.includes('?') ? '&' : '?'}filter[${
                getOptions?.searchParam || 'q'
            }]=${v}`;
            if (getOptions?.disableCache) {
                getData(url, userToken, lang).then(callBack);
            } else {
                getMemoizedData(url, userToken, lang).then(callBack);
            }
        } else {
            setGetAllListFinded(
                list.filter((item) => {
                    const title = getLabel(item);
                    return title?.toLowerCase().includes(v?.toLowerCase());
                })
            );
            findList(
                list.filter((item) => {
                    const title = getLabel(item);
                    return title?.toLowerCase().includes(v?.toLowerCase());
                })
            );
        }
    };
    if (getOptions?.searchUrl) {
        setLoading(true);
        timeout.current = setTimeout(func, 500);
    } else {
        func();
    }
};

export const getOptionsToCopy = (valueOption, multiple) => {
    if (multiple && valueOption?.length) {
        return valueOption.map((option) => getLabel(option)).join(',');
    }
    return getLabel(valueOption) || '';
};
